import type { FmbThaliRow } from "./AddThalisDialog";

/** API `searchThalis` max page size. */
export const SELECT_ALL_MATCHING_PAGE_SIZE = 100;

/** Safety cap so an unfiltered tenant-wide select cannot unbounded-fetch. */
export const SELECT_ALL_MATCHING_MAX_PAGES = 50;

export function thalisSearchParams(opts: {
  search: string;
  tags: string[];
  page: number;
  pageSize: number;
}): URLSearchParams {
  const params = new URLSearchParams();
  params.set("limit", String(opts.pageSize));
  params.set("page", String(opts.page));
  const t = opts.search.trim();
  if (t) params.set("search", t);
  for (const tag of opts.tags) {
    const trimmed = tag.trim();
    if (trimmed) params.append("tag", trimmed);
  }
  return params;
}

/** 0-based page indices to cover `totalCount` at `pageSize`. */
export function searchPageIndices(
  totalCount: number,
  pageSize: number,
  maxPages: number
): number[] {
  if (totalCount <= 0 || pageSize <= 0 || maxPages <= 0) return [];
  const pages = Math.min(Math.ceil(totalCount / pageSize), maxPages);
  return Array.from({ length: pages }, (_, i) => i);
}

export function shouldShowSelectAllMatching(
  totalCount: number,
  currentPageRowCount: number
): boolean {
  return totalCount > currentPageRowCount && currentPageRowCount >= 0;
}

/**
 * Update selection for the visible page only. Off-page ids in `prev` are kept
 * so header select-all / deselect-all does not wipe cross-page selection.
 */
export function mergePageSelectionIntoPending(
  prev: Record<string, FmbThaliRow>,
  pageRows: FmbThaliRow[],
  selectedIds: Array<string | number>,
  assignedThaliIdSet: Set<string>
): Record<string, FmbThaliRow> {
  const pageIdSet = new Set(
    pageRows.map((t) => (t.id != null && t.id !== "" ? String(t.id) : "")).filter(Boolean)
  );
  const selectedOnPage = new Set(
    selectedIds.map(String).filter((id) => id !== "" && pageIdSet.has(id))
  );
  const next = { ...prev };
  for (const pageId of pageIdSet) {
    if (!selectedOnPage.has(pageId)) delete next[pageId];
  }
  for (const id of selectedOnPage) {
    if (assignedThaliIdSet.has(id) || next[id]) continue;
    const row = pageRows.find((t) => String(t.id) === id);
    if (row) next[id] = row;
  }
  return next;
}

export function addSelectableRowsToPending(
  prev: Record<string, FmbThaliRow>,
  rows: FmbThaliRow[],
  assignedThaliIdSet: Set<string>
): Record<string, FmbThaliRow> {
  const next = { ...prev };
  for (const row of rows) {
    const id = row.id != null && row.id !== "" ? String(row.id) : "";
    if (!id || assignedThaliIdSet.has(id)) continue;
    next[id] = row;
  }
  return next;
}
